import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, LogOut } from 'lucide-react';
import { useAuth } from './AuthContext';
import logger from '../../utils/logger';
import { isMobileAdminAppView } from '../../lib/appView';

const InactivityDetector = () => {
  const { logout, user } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes en secondes
  const [isPlaying, setIsPlaying] = useState(false);
  
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const showWarningRef = useRef(false); // Ref pour éviter la boucle du useEffect
  
  // Durée d'inactivité depuis les préférences utilisateur (en minutes)
  const inactivityMinutes = user?.inactivity_timeout || 30;
  const INACTIVITY_TIME = inactivityMinutes * 60 * 1000; // Conversion en millisecondes
  const WARNING_TIME = 5 * 60 * 1000; // 5 minutes en millisecondes
  
  // Lire les préférences depuis user
  const soundEnabled = user?.inactivity_sound_enabled !== false;
  const animationEnabled = user?.inactivity_animation_enabled !== false;

  // Initialiser l'audio — uniquement pour les admins/super_admins.
  // Sinon, un client entendait un bref « ronflement » au premier clic
  // (le déblocage autoplay lançait play() de façon asynchrone).
  useEffect(() => {
    const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin') && !isMobileAdminAppView();
    if (!isAdmin) return;

    audioRef.current = new Audio('/inactivity-sound.wav');
    audioRef.current.loop = true;
    
    // Débloquer l'autoplay audio dès la première interaction
    const unlockAudio = () => {
      if (audioRef.current) {
        audioRef.current.play().then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }).catch(() => {
          // Silencieux si échec
        });
        document.removeEventListener('click', unlockAudio);
        document.removeEventListener('keydown', unlockAudio);
      }
    };
    
    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('keydown', unlockAudio, { once: true });
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, [user]);

  // Jouer le son (seulement si activé dans les préférences)
  const playSound = () => {
    if (soundEnabled && audioRef.current && !isPlaying) {
      audioRef.current.play().catch(err => {
        logger.error('Erreur lecture audio:', err);
      });
      setIsPlaying(true);
    }
  };

  // Arrêter le son
  const stopSound = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  // Réinitialiser tous les timers
  const resetTimers = () => {
    // Nettoyer les anciens timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Masquer le warning et arrêter le son
    setShowWarning(false);
    showWarningRef.current = false;
    stopSound();
    setCountdown(300);

    // Persiste l'horodatage de dernière activité (contrôle au retour / onglet fermé).
    try { localStorage.setItem('pitboard_last_activity', String(Date.now())); } catch { /* ignore */ }

    // Démarrer un nouveau timer d'inactivité
    inactivityTimerRef.current = setTimeout(() => {
      showInactivityWarning();
    }, INACTIVITY_TIME);
  };

  // Afficher l'avertissement d'inactivité
  const showInactivityWarning = () => {
    setShowWarning(true);
    showWarningRef.current = true;
    playSound();
    setCountdown(300); // Reset à 5 minutes

    // Démarrer le compte à rebours
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Timer pour déconnexion automatique après 5 min
    warningTimerRef.current = setTimeout(() => {
      handleLogout();
    }, WARNING_TIME);
  };

  // Gérer la confirmation de présence
  const handleStillHere = () => {
    stopSound();
    setShowWarning(false);
    setCountdown(300);
    
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    resetTimers();
  };

  // Gérer la déconnexion
  const handleLogout = () => {
    stopSound();
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    logout();
  };

  // Formater le temps restant
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Écouter les événements d'activité
  useEffect(() => {
    // Uniquement pour les admins et super_admins.
    // Désactivé sur la web app mobile admin (sécurité de l'appareil suffisante).
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin') || isMobileAdminAppView()) {
      return;
    }

    // Événements de vraie interaction utilisateur uniquement (pas de requêtes API)
    const events = ['mousedown', 'keypress', 'click'];
    
    const handleActivity = () => {
      // Utiliser la ref pour éviter la boucle de re-render
      if (!showWarningRef.current) {
        resetTimers();
      }
    };

    // Ajouter les écouteurs d'événements
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Démarrer le timer initial
    resetTimers();

    // Nettoyer lors du démontage
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      
      stopSound();
    };
  }, [user]);

  // Ne rien afficher si pas admin/super_admin, sur mobile app, ou pas de warning
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin') || isMobileAdminAppView() || !showWarning) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]">
      <div className={`bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 ${animationEnabled ? 'animate-bounce hover:animate-none' : ''}`}>
        {/* Icône d'alerte */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
            <AlertCircle size={48} className="text-orange-600" />
          </div>
        </div>

        {/* Titre */}
        <h2 className="text-2xl font-bold text-center text-slate-900 mb-4">
          😴 Êtes-vous toujours là ?
        </h2>

        {/* Message */}
        <p className="text-center text-slate-600 mb-6">
          Vous semblez inactif depuis {inactivityMinutes === 60 ? '1 heure' : `${inactivityMinutes} minutes`}. Si vous ne répondez pas, vous serez déconnecté automatiquement.
        </p>

        {/* Compte à rebours */}
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-6">
          <p className="text-center text-red-600 text-sm font-medium mb-2">
            Déconnexion automatique dans :
          </p>
          <p className="text-center text-4xl font-bold text-red-600">
            {formatTime(countdown)}
          </p>
        </div>

        {/* Boutons */}
        <div className="space-y-3">
          <button
            onClick={handleStillHere}
            className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all font-medium text-lg flex items-center justify-center space-x-2"
          >
            <span>✅ Oui, je suis là !</span>
          </button>
          
          <button
            onClick={handleLogout}
            className="w-full px-6 py-3 bg-slate-200 text-slate-700 rounded-xl hover:bg-slate-300 transition-colors font-medium flex items-center justify-center space-x-2"
          >
            <LogOut size={20} />
            <span>Me déconnecter</span>
          </button>
        </div>

        {/* Indicateur sonore */}
        <p className="text-center text-xs text-slate-500 mt-4">
          🔊 Son de notification activé
        </p>
      </div>
    </div>
  );
};

export default InactivityDetector;
